import { useRef, useState } from 'react'

import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    useBoolean,
    useBreakpointValue,
    useOutsideClick,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useResetForgotPasswordMutation } from '@core/app/api/authApi'
import { code, email, nextForgotStep } from '@core/app/slices/auth'
import { PasswordEyeIcon } from '@core/assets/icons/password_eye'
import { PasswordEyeIconSlash } from '@core/assets/icons/password_eye_slash'
import checkedImage from '@core/assets/images/validation_checked.svg'
import { AppButton } from '@core/components/AppButton'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'

import { Validation } from '@page/signup/components/registerFlow/createProfile/validation'

export const NewPassword = () => {
    const dispatch = useAppDispatch()
    const appCode = useAppSelector(code)
    const emailValue = useAppSelector(email)
    const [resetPassword] = useResetForgotPasswordMutation()
    const passwordRef = useRef<any>()
    const [isPasswordPopover, setIsPasswordPopover] = useBoolean()
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [showRepeatPassword, setShowRepeatPassword] = useState<boolean>(false)
    const placement: PlacementWithLogical = useBreakpointValue({
        base: 'bottom',
        sm: 'bottom',
        md: 'bottom',
        lg: 'bottom',
        xl: 'left',
    })

    const { executeRecaptcha } = useGoogleReCaptcha()
    const { t } = useTranslation()

    useOutsideClick({
        ref: passwordRef,
        handler: () => setIsPasswordPopover.off(),
    })

    const resetPasswordHandler = async (password: string, setStatus: Function, setSubmitting: Function) => {
        if (!executeRecaptcha) {
            console.error('ReCAPTCHA has not loaded yet')
            return
        }

        const token = await executeRecaptcha('api_reset_password_reset')

        if (token) {
            try {
                await resetPassword({
                    data: {
                        email: emailValue,
                        code: appCode,
                        password,
                        'g-recaptcha-response': token,
                    },
                }).unwrap()

                dispatch(nextForgotStep('passwordReset'))
            } catch (error: any) {
                if (error?.status === 422) {
                    const apiMessage = error?.message || 'Invalid Recaptcha. Please, reload the page and try again.'
                    setStatus({
                        password: apiMessage,
                    })
                } else {
                    setStatus({
                        password: error.data?.errors?.code[0] || 'unknown error',
                    })
                }
            } finally {
                setSubmitting(false)
            }
        }
    }

    return (
        <Flex flexDirection={'column'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} 3 {t('1165')} 4
            </Text>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'} mb={6}>
                {t('1547')}
            </Text>
            <Formik
                initialValues={{
                    password: '',
                    repeatPassword: '',
                }}
                validationSchema={Yup.object({
                    password: Yup.string()
                        .required(`${t('6599')}`)
                        .matches(/\p{Lu}/u, `${t('9327')}`) // 9327 - Password must contain at least one capital letter
                        .matches(/\p{Ll}/u, `${t('9328')}`) // 9328 - Password must contain at least one lowercase letter
                        .matches(/\d/, `${t('9326')}`) // 9326 - Password must contain at least one number
                        .matches(/[\p{P}\p{S}]/u, `${t('9325')}`) // 9325 - Password must contain at least one special character
                        .matches(/^\S*$/, `${t('9324')}`) // 9324 - Password must not contain spaces
                        .min(8, `${t('5092')}`)
                        .max(64, `${t('9323')}`),
                    repeatPassword: Yup.string()
                        .required(`${t('7093')}`)
                        .oneOf([Yup.ref('password'), null], `${t('6445')}`),
                })}
                onSubmit={(values, { setSubmitting, setStatus }) => {
                    resetPasswordHandler(values.password, setSubmitting, setStatus)
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    isSubmitting,
                    handleBlur,
                    handleSubmit,
                    handleChange,
                    status,
                    setStatus,
                    setFieldValue,
                }) => (
                    <Form onSubmit={handleSubmit} style={{ marginBottom: '0px' }}>
                        <FormControl mb={4} isInvalid={!!errors.password && touched.password}>
                            <FormLabel>{t('423')}*</FormLabel>
                            <Popover
                                isLazy
                                placement={placement}
                                initialFocusRef={passwordRef}
                                isOpen={isPasswordPopover}
                            >
                                <PopoverTrigger>
                                    <InputGroup>
                                        <Input
                                            ref={passwordRef}
                                            type={showPassword ? 'text' : 'password'}
                                            name={'password'}
                                            value={values.password}
                                            onChange={(e) => {
                                                e.target.value.length > 0
                                                    ? setIsPasswordPopover.on()
                                                    : setIsPasswordPopover.off()
                                                setFieldValue('password', e.target.value.trim())
                                            }}
                                            onBlur={(e) => {
                                                handleBlur(e)
                                                setIsPasswordPopover.off()
                                            }}
                                            placeholder={`${t('699')}`}
                                        />
                                        <InputRightElement>
                                            <Button
                                                variant={'unstyled'}
                                                onClick={() => setShowPassword((prev: any) => !prev)}
                                                aria-label={showRepeatPassword ? `${t('7850')}` : `${t('7849')}`}
                                            >
                                                {showPassword ? (
                                                    <PasswordEyeIconSlash width={4} height={4} />
                                                ) : (
                                                    <PasswordEyeIcon width={4} height={4} />
                                                )}
                                            </Button>
                                        </InputRightElement>
                                    </InputGroup>
                                </PopoverTrigger>
                                <PopoverContent width={'276px'} display={{ base: 'none', xl: 'block' }}>
                                    <PopoverArrow />
                                    <PopoverBody px={4} py={3}>
                                        <Validation password={values.password} />
                                    </PopoverBody>
                                </PopoverContent>
                            </Popover>
                            {isPasswordPopover && (
                                <Box display={{ base: 'block', xl: 'none' }}>
                                    <Validation password={values.password} />
                                </Box>
                            )}

                            <FormErrorMessage>
                                {errors.password && touched.password && errors.password}
                            </FormErrorMessage>
                        </FormControl>
                        <FormControl mb={6} isInvalid={!!errors.repeatPassword && touched.repeatPassword}>
                            <FormLabel>{t('3310')}*</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showRepeatPassword ? 'text' : 'password'}
                                    name={'repeatPassword'}
                                    value={values.repeatPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder={`${t('3310')}`}
                                />
                                <InputRightElement>
                                    <Button
                                        variant={'unstyled'}
                                        onClick={() => setShowRepeatPassword((prev: any) => !prev)}
                                        aria-label={showRepeatPassword ? `${t('7850')}` : `${t('7849')}`}
                                    >
                                        {showRepeatPassword ? (
                                            <PasswordEyeIconSlash width={4} height={4} />
                                        ) : (
                                            <PasswordEyeIcon width={4} height={4} />
                                        )}
                                    </Button>
                                </InputRightElement>
                            </InputGroup>
                            <FormErrorMessage>
                                {errors.repeatPassword && touched.repeatPassword && errors.repeatPassword}
                            </FormErrorMessage>
                            {!errors.repeatPassword &&
                                !errors.password &&
                                values.repeatPassword === values.password && (
                                    <HStack gap={2} mt={'4px'}>
                                        <Image src={checkedImage} />
                                        <Text fontSize={'12px'} color={'text.secondary'}>
                                            {t('11255', 'Passwords match')}{' '}
                                        </Text>
                                    </HStack>
                                )}
                        </FormControl>
                        <FormControl mt={6} display={'flex'} justifyContent={'end'}>
                            {/*<Button*/}
                            {/*    type={'submit'}*/}
                            {/*    disabled={isSubmitting}*/}
                            {/*    variant={'primary'}*/}
                            {/*    w={'120px'}*/}
                            {/*    h={'44px'}*/}
                            {/*    bg={appTheme ? `#${appTheme.newSettings.general.ButtonBackground}` : 'btn.primary'}*/}
                            {/*    color={appTheme ? `#${appTheme.newSettings.general.ButtonText}` : 'text.white'}*/}
                            {/*    _hover={{*/}
                            {/*        bg: appTheme ? `#${appTheme.newSettings.general.ButtonText}` : 'text.white',*/}
                            {/*    }}*/}
                            {/*>*/}
                            {/*    {t('297')}*/}
                            {/*</Button>*/}
                            <AppButton
                                type={'submit'}
                                disabled={isSubmitting}
                                variant={'primary'}
                                w={'120px'}
                                h={'44px'}
                                title={t('297')}
                            ></AppButton>
                        </FormControl>
                    </Form>
                )}
            </Formik>
        </Flex>
    )
}
